import React, { useState, useEffect, useRef } from "react";
import {
  createPlaylist,
  updatePlaylist,
  getPlaylists,
} from "../../../network/service";
import { useSelector } from "react-redux";
import "../scss/_addToPlaylistPopup.scss";
import { toast } from "react-toastify";
import Translate, { useTranslateText } from "../../../Components/Multilanguage/Translate";
import ConfirmationPopup from "../../../Components/ConfirmationPopup/ConfirmationPopup";
import Util from "../../../Components/ShowCard/util";

function AddToPlaylistPopup({
  isOpen,
  onClose,
  showDetails,
  videoId,
  setWatchlistStatus,
  type = "add", // "add" or "delete"
}) {
  const translateText = useTranslateText();
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
  };
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [playlists, setPlaylists] = useState([]);
  const [loadingCreatePlaylist, setLoadingCreatePlaylist] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [deletingPlaylists, setDeletingPlaylists] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const playlistListRef = useRef(null);
  const { deletePlaylist } = Util();

  const handleCheckboxChange = (playlistId) => {
    setPlaylists((prev) =>
      prev.map((playlist) =>
        playlist.id === playlistId
          ? { ...playlist, selected: !playlist.selected }
          : playlist
      )
    );
  };

  const handleCreatePlaylist = async () => {
    setLoadingCreatePlaylist(true);
    if (newPlaylistName.trim()) {
      try {
        const response = await createPlaylist(appInfo, newPlaylistName);
        if (response?.status === 200) {
          fetchPlaylists();
          toast.success(response?.data?.message, {
            position: "bottom-left",
          });
          setLoadingCreatePlaylist(false);
        } else {
          toast.error((translateText("try_again_msg")), {
            position: "bottom-left",
          });
          setLoadingCreatePlaylist(false);
        }
      } catch (error) {
        toast.error((translateText("try_again_msg")), {
          position: "bottom-left",
        });
        setLoadingCreatePlaylist(false);
      }
      setNewPlaylistName("");
    }
  };

  const handleSave = async () => {
    let selectedPlaylists = playlists
      .filter((p) => p.selected)
      .map((p) => p.id);

    selectedPlaylists = selectedPlaylists.length > 0 ? selectedPlaylists : [];
    try {
      const response = await updatePlaylist(
        appInfo,
        showDetails?.show_id,
        selectedPlaylists,
        videoId
      );
      if (response?.status === 200) {
        if (response?.data?.data?.watchlist_flag === 1) {
          setWatchlistStatus("added");
        } else {
          setWatchlistStatus("removed");
        }
        toast.success(response?.data?.message, {
          position: "bottom-left",
        });
        if (onClose) {
          onClose();
        }
      } else {
        toast.error((translateText("try_again_msg")), { position: "bottom-left" });
      }
    } catch (error) {
      toast.error((translateText("try_again_msg")), { position: "bottom-left" });
    }
  };

  const handleDelete = () => {
    const selectedPlaylists = playlists.filter((p) => p.selected);
    if (selectedPlaylists.length === 0) {
      return;
    }
    
    // Close the current popup first, then show confirmation
    if (onClose) {
      onClose();
    }
    setShowConfirmation(true);
  };

  const confirmDelete = async () => {
    setDeletingPlaylists(true);
    let selectedPlaylists = playlists
      .filter((p) => p.selected)
      .map((p) => p.id);

    selectedPlaylists = selectedPlaylists.length > 0 ? selectedPlaylists : [];
    
    try {
      const response = await deletePlaylist(
        appInfo,
        selectedPlaylists
      );
      if (response?.status === 200) {
        setDeletingPlaylists(false);
        toast.success(response?.data?.message, {
          position: "bottom-left",
        });
        if (onClose) {
          onClose();
        }
      } else {
        setDeletingPlaylists(false);
        toast.error(translateText("try_again_msg"), { position: "bottom-left" });
      }
    } catch (error) {
      toast.error(translateText("try_again_msg"), { position: "bottom-left" });
      setDeletingPlaylists(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  const fetchPlaylists = async () => {
    try {
      const response = await getPlaylists(appInfo);
      if (response?.status === 200) {
        const playlistsData = response.data.data.map((playlist) => {
          const idToCheck = videoId || showDetails?.show_id;
          const isShowInPlaylist = type === "add" ? (
            playlist.shows?.includes(idToCheck) || 
            (videoId && playlist.videos?.includes(videoId))
          ) : false;

          return {
            id: playlist.id,
            name: playlist.title,
            selected: isShowInPlaylist,
          };
        }).filter(playlist => type === "delete" ? playlist.id !== 0 : true);

        setPlaylists(playlistsData);
        // Scroll the playlist list to top after successful creation
        if (playlistListRef.current) {
          playlistListRef.current.scrollTop = 0;
        }
        setLoadingPlaylists(false);
      }
    } catch (error) {
      console.error("Failed to fetch playlists:", error);
      setLoadingPlaylists(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setLoadingPlaylists(true);
      setNewPlaylistName("");
      fetchPlaylists();
    }
  }, [isOpen]);

  return (
    <>
      <ConfirmationPopup
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onContinue={confirmDelete}
        questionText={translateText("delete_playlist_confirmation") || "Are you sure you want to delete the selected playlist(s)? This action cannot be undone."}
        cancelText={translateText("cancel") || "Cancel"}
        continueText={translateText("delete") || "Delete"}
      />
      {isOpen && (
        <>
          <div className="playlist-popup-overlay" onClick={handleCancel}></div>
      <div className="playlist-popup">
        {/* Header */}
        <div className="playlist-popup__header">
          <h1 className="playlist-popup__title">
            <Translate textKey={type === "delete" ? "delete_playlist" : "add_to_list"} />
          </h1>
          <button
            className="playlist-popup__close-btn"
            onClick={handleCancel}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="playlist-popup__content">
          <div className="playlist-popup__divider">
            <div className="playlist-popup__divider-line"></div>
          </div>

          {/* Playlist List */}
          <div className="playlist-popup__list" ref={playlistListRef}>
            {loadingPlaylists ? (
              <div className="playlist-loader">
                <div className="playlist-loading-spinner"></div>
              </div>
            ) : playlists.length === 0 ? (
              <div className="playlist-empty-state">
                <p className="playlist-empty-message">
                  {translateText("no_playlists_available")}
                </p>
              </div>
            ) : (
              playlists.map((playlist) => (
                <label key={playlist.id} className="playlist-item">
                  <div className="playlist-item__content">
                    <p className="playlist-item__name">{playlist.name}</p>
                  </div>
                  <div className="playlist-item__checkbox-wrapper">
                    <input
                      type="checkbox"
                      className="custom-checkbox"
                      checked={playlist.selected}
                      onChange={() => handleCheckboxChange(playlist.id)}
                    />
                  </div>
                </label>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="playlist-popup__footer">
          {/* Create New Playlist - Only show in add mode */}
          {type === "add" && (
            <div className="playlist-popup__create-section">
              <div className="create-playlist">
                <div className="create-playlist__input-section">
                  <input
                    type="text"
                    className="create-playlist__input"
                    placeholder={translateText("create_new_playlist")}
                    value={newPlaylistName}
                    onChange={(e) => setNewPlaylistName(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && handleCreatePlaylist()
                    }
                  />
                </div>
                <button
                  className="create-playlist__btn"
                  onClick={handleCreatePlaylist}
                  disabled={!newPlaylistName.trim() || loadingCreatePlaylist}
                >
                  <span>
                    <Translate textKey={"create"} />
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="playlist-popup__actions">
            <button className="btn btn--cancel" onClick={handleCancel}>
              <span>
                <Translate textKey={"cancel"} />
              </span>
            </button>
            {type === "delete" ? (
              <button 
                className="btn btn--primary" 
                disabled={deletingPlaylists || playlists.filter(playlist => playlist.selected).length === 0} 
                onClick={handleDelete}
              >
                <span>
                  <Translate textKey={"delete"} />
                </span>
              </button>
            ) : (
              <button className="btn btn--primary" onClick={handleSave}>
                <span>
                  <Translate textKey={"save"} />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </>
  );
}

export default AddToPlaylistPopup;
